import React, { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useSocket } from "hooks/useSocket";
import { useUser } from "util/UserContext";
import { useNotificationsModal } from "context/NotificationsModalContext";
import ChatPreviewToast, { type ChatPreviewToastVariant } from "components/Chat/ChatPreviewToast";
import NotificationPreviewToast from "components/Notifications/NotificationPreviewToast";
import { REALTIME_TOAST_OPTIONS } from "ui/Sonner";
import {
  toActorFromChatActor,
} from "util/actorIdentity";
import { chatQueryKeys } from "hooks/queries/useChatQueries";
import type { ChatActorLite, ChatConversation, ChatMessage } from "types/chat";
import type { Actor } from "types/actor";
import type { Notification } from "types/notification";

/**
 * Cross-tab broadcast channel name used by the realtime toast layer to
 * dedupe identical toasts when the same user has multiple open tabs.
 * Keeping the channel name in a constant prevents typos drift between
 * the producer and consumer sides.
 */
const REALTIME_TOAST_CHANNEL = "eoe-realtime-toasts";

/**
 * Window (in ms) during which a `claim` for a `dedupeKey` is considered
 * authoritative. After the window the key may be re-claimed; this is
 * defensive only - in practice each socket event arrives once per user
 * and the BroadcastChannel propagates within a few ms.
 */
const DEDUPE_CLAIM_WINDOW_MS = 800;

/**
 * Coerce a value that may be a number, a numeric string (PostgreSQL
 * returns `BIGINT` columns as strings by default), or absent into a
 * positive integer or `null`. Used to normalize ids that arrive over
 * the WebSocket payload without forcing the producer to know about pg
 * driver quirks.
 */
const toPositiveInt = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isInteger(value) && value > 0) {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isInteger(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return null;
};

/**
 * Suppression reasons. Surface as the second argument of
 * `logSuppressed(...)` so we can troubleshoot "why didn't I see a
 * toast?" without raising the log level for everyone.
 *
 * Note: we intentionally do NOT suppress toasts while the notifications
 * modal is open. Users who have the modal open expect realtime toasts
 * to keep arriving (they may have it minimized / partially visible).
 * The modal already updates its list via TanStack Query invalidation,
 * so the toast is additive, not duplicative.
 */
type SuppressionReason =
  | "self-actor"
  | "self-sender"
  | "viewing-conversation"
  | "muted-conversation"
  | "muted-subscription"
  | "redundant-with-chat-toast"
  | "duplicate-tab"
  | "missing-payload"
  | "no-current-user";

/**
 * Notification types that are already surfaced by the dedicated
 * `ChatPreviewToast` because they have a corresponding `chat:message`
 * or `chat:request` socket event. We suppress the generic
 * `NotificationPreviewToast` for these so a single chat event does not
 * stack two toasts on top of each other (rich chat preview AND generic
 * "New chat update").
 *
 * `chat_reaction` and `chat_poll_vote` are intentionally excluded -
 * they do NOT have a dedicated chat toast, so the notification toast
 * is the only realtime UI for those events.
 */
const CHAT_TYPES_HANDLED_BY_CHAT_TOAST: ReadonlySet<string> = new Set([
  "chat_new_message",
  "chat_mention",
  "chat_thread_reply",
  "chat_request",
]);

interface ChatMessagePayload {
  conversationPublicId?: string;
  message?: ChatMessage;
}

interface ChatRequestPayload {
  conversationPublicId?: string;
  requestId?: number;
  message?: ChatMessage;
}

/**
 * Cross-tab message envelope. `claim` lets the originating tab announce
 * "I am about to show this toast"; other tabs that receive the claim
 * skip the corresponding event.
 */
interface RealtimeToastBroadcast {
  type: "claim";
  dedupeKey: string;
  ts: number;
}

/**
 * Local diagnostic logger. Uses `console.info` (not debug) so the
 * default DevTools log level surfaces it without the user opting in.
 * Each line is prefixed with `[realtime-toast]` so console filtering
 * is straightforward when troubleshooting.
 */
const logSuppressed = (reason: SuppressionReason, dedupeKey: string): void => {
  if (typeof console !== "undefined" && typeof console.info === "function") {
    console.info("[realtime-toast] suppressed", { reason, dedupeKey });
  }
};

/**
 * Diagnostic log helper for non-suppression events (received,
 * displayed, errors). Same prefix and level as `logSuppressed` so all
 * realtime-toast traffic shows up under one filter.
 */
const logTrace = (event: string, payload?: Record<string, unknown>): void => {
  if (typeof console !== "undefined" && typeof console.info === "function") {
    if (payload) {
      console.info(`[realtime-toast] ${event}`, payload);
    } else {
      console.info(`[realtime-toast] ${event}`);
    }
  }
};

/**
 * Build a stable dedupe key for a notification. The notification id is
 * unique per recipient so collisions across users are impossible.
 */
const buildNotificationDedupeKey = (notification: Notification): string => {
  return `notif:${notification.id}`;
};

/**
 * Build a stable dedupe key for a chat message-style event. Falls back
 * to the conversation id when `message.public_id` is missing on a
 * legacy payload, which still gives a reasonable per-message guarantee
 * in practice.
 */
const buildChatDedupeKey = (
  type: "message" | "request",
  conversationPublicId: string,
  message: ChatMessage | undefined,
  requestId: number | undefined,
): string => {
  if (type === "request" && typeof requestId === "number") {
    return `chat-request:${requestId}`;
  }
  if (message && typeof message.public_id === "string" && message.public_id.length > 0) {
    return `chat:${message.public_id}`;
  }
  return `chat:${conversationPublicId}:${Date.now()}`;
};

/**
 * Compose an `Actor` for the notification toast from the various actor
 * fields on a notification row. Mirrors the helper in
 * `NotificationsModal` so the avatar resolves identically.
 */
const buildNotificationActor = (notification: Notification): Actor | null => {
  const id = Number(notification.actor_id);
  if (!Number.isInteger(id) || id <= 0) {
    return null;
  }
  const username = (notification.actor_name || "").trim();
  if (!username) {
    return null;
  }
  const avatarKey = (notification.actor_properties_public?.avatar_key || "").trim();
  return {
    id,
    kind: notification.actor_type === "team" ? "team" : "user",
    username,
    displayName:
      typeof notification.actor_display_name === "string" && notification.actor_display_name.length > 0
        ? notification.actor_display_name
        : null,
    avatarKey: avatarKey.length > 0 ? avatarKey : null,
  };
};

/**
 * Look up the cached `ChatConversation` for a given public id without
 * triggering a fetch. Used by the suppression rules so we can respect
 * `muted_until` and pull a friendly conversation title when warm.
 *
 * Returns `null` when no cached conversation list contains a matching
 * row, which is the common case for fresh sessions.
 */
const findCachedConversation = (
  cache: ReturnType<typeof useQueryClient>,
  conversationPublicId: string,
): ChatConversation | null => {
  if (!conversationPublicId) {
    return null;
  }
  const conversationListsByKey = cache.getQueriesData<{ conversations: ChatConversation[] }>({
    queryKey: ["chat", "conversations"],
  });
  for (const [, data] of conversationListsByKey) {
    const conversations = data?.conversations;
    if (!conversations) {
      continue;
    }
    const match = conversations.find((c) => c.public_id === conversationPublicId);
    if (match) {
      return match;
    }
  }
  const direct = cache.getQueryData<ChatConversation>(chatQueryKeys.conversation(conversationPublicId));
  return direct ?? null;
};

/**
 * Returns true when the conversation is muted right now per its
 * `muted_until` field. Treats invalid or past dates as not muted.
 */
const isConversationMuted = (conversation: ChatConversation | null): boolean => {
  if (!conversation || !conversation.muted_until) {
    return false;
  }
  const mutedUntilMs = Date.parse(conversation.muted_until);
  if (Number.isNaN(mutedUntilMs)) {
    return false;
  }
  return mutedUntilMs > Date.now();
};

/**
 * Resolve the visible conversation title to use in the chat toast.
 * Prefers the cached conversation title (group/team) and falls back to
 * the sender's display name for direct chats with no cached row.
 */
const resolveConversationTitle = (
  conversation: ChatConversation | null,
  fallback: string,
): string => {
  if (!conversation) {
    return fallback;
  }
  if (conversation.title && conversation.title.trim().length > 0) {
    return conversation.title.trim();
  }
  if (conversation.team) {
    const display = conversation.team.display_name || conversation.team.teamname;
    return display || fallback;
  }
  return fallback;
};

/**
 * Returns the variant of chat toast to render based on the cached
 * conversation `kind` (when known) and the event type. Falls back to
 * `direct` when no conversation cache is present and the event is a
 * normal message.
 */
const resolveChatVariant = (
  eventType: "message" | "request",
  conversation: ChatConversation | null,
): ChatPreviewToastVariant => {
  if (eventType === "request") {
    return "request";
  }
  if (conversation && (conversation.kind === "group" || conversation.kind === "team")) {
    return "group";
  }
  return "direct";
};

/**
 * Resolve the sender display name from the event payload. Prefers the
 * server-supplied `display_name`, falls back to `username`.
 */
const resolveSenderName = (sender: ChatActorLite | null | undefined): string => {
  if (!sender) {
    return "";
  }
  const displayName = (sender.display_name || "").trim();
  if (displayName.length > 0) {
    return displayName;
  }
  return (sender.username || "").trim();
};

/**
 * `useRealtimeToasts` is a non-rendering hook that wires the existing
 * shared Socket.IO connection into the single `<Toaster>` mounted in
 * `App.tsx`. Realtime previews opt into the top-center surface via
 * `REALTIME_TOAST_OPTIONS` (passed per `toast.custom` call) so that
 * existing transactional `toast.success(...)` / `toast.error(...)`
 * calls keep rendering at the bottom-right default. Mount it once
 * near the app shell so it has access to the router, the user
 * context, and the notifications modal controller.
 *
 * The hook is intentionally side-effect-only: it does not render any
 * UI, does not own routing state, and never opens a second socket
 * connection. All UI shape lives in the toast components, all routing
 * lives in `useNavigate`, and the modal controller lives in
 * `NotificationsModalContext`.
 */
export const useRealtimeToasts = (): void => {
  const { socket } = useSocket();
  const { currentUser } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { isOpen: isNotificationsModalOpen, open: openNotificationsModal } = useNotificationsModal();

  // Refs so the socket-event handlers (which we register once) can read
  // the latest values without re-subscribing on every state change. The
  // alternative - re-registering listeners on every render - would
  // create transient gaps where events arrive but the handler is gone.
  const currentUserIdRef = useRef<number | null>(currentUser?.id ?? null);
  const locationPathnameRef = useRef<string>(location.pathname);

  useEffect(() => {
    currentUserIdRef.current = currentUser?.id ?? null;
  }, [currentUser?.id]);
  useEffect(() => {
    locationPathnameRef.current = location.pathname;
  }, [location.pathname]);

  // `isNotificationsModalOpen` is read by `useNotificationsModal()` for
  // future suppression rules; today we deliberately do not suppress on
  // it (see `SuppressionReason` doc above). Reference once to avoid
  // an unused-var lint while keeping the destructure idiomatic.
  void isNotificationsModalOpen;

  // Cross-tab dedupe state. `claimedKeys` tracks recent keys we have
  // either shown or heard claimed by another tab; the timestamp lets us
  // age out stale entries so memory does not grow unbounded.
  const claimedKeysRef = useRef<Map<string, number>>(new Map());
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") {
      // Older browsers or non-browser environments (SSR, jsdom without
      // polyfill). Hook still works, just without cross-tab dedupe.
      return undefined;
    }
    const channel = new BroadcastChannel(REALTIME_TOAST_CHANNEL);
    broadcastChannelRef.current = channel;

    const handleMessage = (event: MessageEvent<RealtimeToastBroadcast>) => {
      const data = event.data;
      if (!data || data.type !== "claim" || typeof data.dedupeKey !== "string") {
        return;
      }
      claimedKeysRef.current.set(data.dedupeKey, Date.now());
    };

    channel.addEventListener("message", handleMessage);
    return () => {
      channel.removeEventListener("message", handleMessage);
      channel.close();
      broadcastChannelRef.current = null;
    };
  }, []);

  /**
   * Try to claim a dedupe key. Returns true when this tab should show
   * the toast (no recent claim seen) and false when another tab has
   * already claimed it within the dedupe window.
   */
  const tryClaim = useCallback((dedupeKey: string): boolean => {
    const now = Date.now();
    const claimedAt = claimedKeysRef.current.get(dedupeKey);
    if (typeof claimedAt === "number" && now - claimedAt < DEDUPE_CLAIM_WINDOW_MS) {
      return false;
    }
    claimedKeysRef.current.set(dedupeKey, now);

    // Best-effort age-out so the map does not grow forever during long
    // sessions. Keep it cheap: only trim when we cross a threshold.
    // `Array.from(...entries())` keeps us compatible with CRA's TypeScript
    // target (no `--downlevelIteration`) without changing semantics.
    if (claimedKeysRef.current.size > 256) {
      Array.from(claimedKeysRef.current.entries()).forEach(([key, ts]) => {
        if (now - ts > DEDUPE_CLAIM_WINDOW_MS * 8) {
          claimedKeysRef.current.delete(key);
        }
      });
    }

    const channel = broadcastChannelRef.current;
    if (channel) {
      try {
        const payload: RealtimeToastBroadcast = {
          type: "claim",
          dedupeKey,
          ts: now,
        };
        channel.postMessage(payload);
      } catch {
        // BroadcastChannel can throw inside structured-clone-restricted
        // contexts; the fallback is "show in this tab" which is fine.
      }
    }
    return true;
  }, []);

  /**
   * Show a notification toast. Pulls the latest user state from refs so
   * the suppression matrix runs against current values even though we
   * registered the listener once.
   *
   * Suppression rules (in order):
   *   1. No current user (signed out / context not initialized).
   *   2. The actor is the current user (don't toast yourself).
   *   3. A muted subscription notification (`subscriptionLevel === 0`).
   *   4. Cross-tab dedupe (another tab already claimed this id).
   *
   * The notifications modal being open is NOT a suppression reason -
   * users may have the modal minimized or partially visible and still
   * want a top-center toast for new arrivals.
   */
  const showNotificationToast = useCallback(
    (notification: Notification): void => {
      const dedupeKey = buildNotificationDedupeKey(notification);
      const userId = currentUserIdRef.current;

      logTrace("notification:received", {
        id: notification?.id,
        type: notification?.notification_type,
        actor_id: notification?.actor_id,
        dedupeKey,
      });

      if (!userId) {
        logSuppressed("no-current-user", dedupeKey);
        return;
      }
      if (Number(notification.actor_id) === Number(userId)) {
        logSuppressed("self-actor", dedupeKey);
        return;
      }
      const notificationType = notification.notification_type || "";
      // Chat-related notifications that arrive alongside a richer
      // `chat:message` / `chat:request` socket event are already
      // surfaced by `ChatPreviewToast`. Suppressing the generic
      // notification toast here prevents the "rich chat preview +
      // generic 'New chat update'" stacking the user reported.
      if (CHAT_TYPES_HANDLED_BY_CHAT_TOAST.has(notificationType)) {
        logSuppressed("redundant-with-chat-toast", dedupeKey);
        return;
      }
      if (
        notificationType.startsWith("subscription_") &&
        notification.properties?.subscriptionLevel === 0
      ) {
        logSuppressed("muted-subscription", dedupeKey);
        return;
      }
      if (!tryClaim(dedupeKey)) {
        logSuppressed("duplicate-tab", dedupeKey);
        return;
      }

      const actor = buildNotificationActor(notification);
      const actorDisplayName =
        (notification.actor_display_name || "").trim() ||
        (notification.actor_name || "").trim() ||
        "";

      try {
        toast.custom(
          (toastId) => (
            <NotificationPreviewToast
              notification={notification}
              actor={actor}
              actorDisplayName={actorDisplayName}
              onClick={() => {
                toast.dismiss(toastId);
                openNotificationsModal();
              }}
              onDismiss={() => toast.dismiss(toastId)}
            />
          ),
          {
            // `REALTIME_TOAST_OPTIONS` carries `position: "top-center"`,
            // duration, sizing, and styling so this single Toaster mount
            // routes the realtime preview to its top-center surface
            // without colliding with bottom-right transactional toasts.
            ...REALTIME_TOAST_OPTIONS,
            id: dedupeKey,
          },
        );
        logTrace("notification:displayed", { dedupeKey });
      } catch (toastError) {
        if (typeof console !== "undefined" && typeof console.error === "function") {
          console.error("[realtime-toast] failed to display notification toast", {
            dedupeKey,
            error: toastError,
          });
        }
      }
    },
    [openNotificationsModal, tryClaim],
  );

  /**
   * Show a chat toast (`message` or `request`). Suppresses when the
   * user is the sender, when the user is currently viewing the matching
   * conversation, when the conversation is muted, or when another tab
   * has already claimed the toast.
   */
  const showChatToast = useCallback(
    (
      eventType: "message" | "request",
      payload: ChatMessagePayload | ChatRequestPayload,
    ): void => {
      const userId = currentUserIdRef.current;
      const conversationPublicId =
        typeof payload.conversationPublicId === "string"
          ? payload.conversationPublicId.trim()
          : "";
      const message = payload.message;
      const requestId = (payload as ChatRequestPayload).requestId;

      const dedupeKey = buildChatDedupeKey(eventType, conversationPublicId, message, requestId);

      logTrace("chat:received", {
        eventType,
        conversationPublicId,
        message_public_id: message?.public_id,
        sender_user_id: message?.sender_user_id,
        dedupeKey,
      });

      if (!userId) {
        logSuppressed("no-current-user", dedupeKey);
        return;
      }
      if (!conversationPublicId || !message) {
        logSuppressed("missing-payload", dedupeKey);
        return;
      }
      // Sender id may arrive as either number or numeric string (pg
      // BIGINT). `Number(...)` normalizes both shapes for the equality
      // check.
      if (
        message.sender_user_id !== null &&
        message.sender_user_id !== undefined &&
        Number(message.sender_user_id) === Number(userId)
      ) {
        logSuppressed("self-sender", dedupeKey);
        return;
      }
      if (locationPathnameRef.current === `/chat/${conversationPublicId}`) {
        logSuppressed("viewing-conversation", dedupeKey);
        return;
      }

      const cachedConversation = findCachedConversation(queryClient, conversationPublicId);
      if (eventType === "message" && isConversationMuted(cachedConversation)) {
        logSuppressed("muted-conversation", dedupeKey);
        return;
      }

      if (!tryClaim(dedupeKey)) {
        logSuppressed("duplicate-tab", dedupeKey);
        return;
      }

      const senderActor = toActorFromChatActor(message.sender);
      const senderName = resolveSenderName(message.sender) || "";
      const conversationTitle = resolveConversationTitle(cachedConversation, senderName);
      const variant = resolveChatVariant(eventType, cachedConversation);
      const bodyPreview = typeof message.body === "string" ? message.body : "";

      try {
        toast.custom(
          (toastId) => (
            <ChatPreviewToast
              senderActor={senderActor}
              senderName={senderName}
              conversationTitle={conversationTitle}
              variant={variant}
              bodyPreview={bodyPreview}
              createdAt={message.created_at}
              onClick={() => {
                toast.dismiss(toastId);
                navigate(`/chat/${conversationPublicId}`);
              }}
              onDismiss={() => toast.dismiss(toastId)}
            />
          ),
          {
            ...REALTIME_TOAST_OPTIONS,
            id: dedupeKey,
          },
        );
        logTrace("chat:displayed", { dedupeKey });
      } catch (toastError) {
        if (typeof console !== "undefined" && typeof console.error === "function") {
          console.error("[realtime-toast] failed to display chat toast", {
            dedupeKey,
            error: toastError,
          });
        }
      }
    },
    [navigate, queryClient, tryClaim],
  );

  // Register socket listeners exactly once per `socket` instance. The
  // refs above let us avoid resubscribing when the user, route, or
  // modal state change.
  useEffect(() => {
    if (!socket) {
      return undefined;
    }
    const handleNotificationNew = (notification: Notification): void => {
      logTrace("socket:notification:new", { id: notification?.id });
      // pg returns BIGINT columns as strings, so the live socket payload
      // can carry `id` as either `number` or numeric `string`. Normalize
      // here instead of rejecting numeric strings outright.
      if (!notification || toPositiveInt(notification.id) === null) {
        logTrace("notification:new rejected: invalid id", { id: notification?.id });
        return;
      }
      showNotificationToast(notification);
    };
    const handleChatMessage = (payload: ChatMessagePayload): void => {
      logTrace("socket:chat:message", {
        conversationPublicId: payload?.conversationPublicId,
        message_public_id: payload?.message?.public_id,
      });
      showChatToast("message", payload);
    };
    const handleChatRequest = (payload: ChatRequestPayload): void => {
      logTrace("socket:chat:request", {
        conversationPublicId: payload?.conversationPublicId,
        requestId: payload?.requestId,
      });
      showChatToast("request", payload);
    };

    logTrace("listeners:registered");
    socket.on("notification:new", handleNotificationNew);
    socket.on("chat:message", handleChatMessage);
    socket.on("chat:request", handleChatRequest);

    return () => {
      socket.off("notification:new", handleNotificationNew);
      socket.off("chat:message", handleChatMessage);
      socket.off("chat:request", handleChatRequest);
    };
  }, [socket, showNotificationToast, showChatToast]);
};

/**
 * Convenience component so the hook can be mounted declaratively in
 * `App.tsx` alongside the other "wrapper" components without a wrapper
 * shim per surface. Returns `null` because the hook is side-effect-only.
 */
export const RealtimeToastsBridge: React.FC = () => {
  useRealtimeToasts();
  return null;
};

export default useRealtimeToasts;
